import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Extended image markdown syntax:
//   ![alt](url)                        default centered block
//   ![alt|left](url)                   left-aligned block
//   ![alt|right](url)                  right-aligned block
//   ![alt|center](url)                 centered block (same as default)
//   ![alt|left|wrap](url)              floated left, text wraps around
//   ![alt|right|wrap](url)             floated right, text wraps around

export default function ImageInsertModal({ onClose, onInsert }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [alt, setAlt] = useState("");
  const [align, setAlign] = useState("center");
  const [layout, setLayout] = useState("block"); // block | wrap
  const [uploading, setUploading] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      e.target.value = "";
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const openPicker = () => fileRef.current?.click();

  const buildSnippet = (url) => {
    const cleanAlt = alt.trim();
    // Compact: only add options when non-default
    const parts = [cleanAlt];
    const nonDefaultAlign = align !== "center";
    const nonDefaultLayout = layout === "wrap";
    if (nonDefaultAlign || nonDefaultLayout) parts.push(align);
    if (nonDefaultLayout) parts.push("wrap");
    return `![${parts.join("|")}](${url})`;
  };

  const submit = async () => {
    if (!file) {
      toast.error("Choose an image first.");
      return;
    }
    if (layout === "wrap" && align === "center") {
      // Wrapping only makes sense with left/right alignment
      toast.error("Wrap requires left or right alignment. Choose one, or switch to 'above and below'.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const snippet = buildSnippet(data.url);
      onInsert(snippet, layout === "wrap");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const radio = (label, value, current, setter, testid) => (
    <label
      className={`cursor-pointer border px-3 py-2 mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
        current === value ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"
      }`}
    >
      <input
        type="radio"
        className="sr-only"
        checked={current === value}
        onChange={() => setter(value)}
        data-testid={testid}
      />
      {label}
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-black w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-insert-modal"
      >
        <div className="flex items-center justify-between border-b border-black px-6 py-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-700">Insert image</div>
            <h3 className="serif text-2xl mt-1">Add a picture</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="image-insert-close"
            className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
          >
            Close
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* File chooser + preview */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Image file</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              data-testid="image-insert-file"
            />
            <div className="mt-2 flex items-start gap-4">
              <button
                type="button"
                onClick={openPicker}
                style={{ backgroundColor: "#fc4707" }}
                className="text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:brightness-90 transition-all"
                data-testid="image-insert-choose"
              >
                {file ? "Replace image" : "Choose image"}
              </button>
              {file && (
                <div className="flex-1 min-w-0">
                  <div className="mono text-xs text-neutral-700 truncate" title={file.name}>{file.name}</div>
                  {preview && (
                    <img src={preview} alt="preview" className="mt-2 max-h-32 border border-neutral-200" />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Alt text */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Alt text (short description for screen readers)
            </label>
            <input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              maxLength={200}
              placeholder="e.g. A crowd holding placards outside Westminster"
              className="mt-2 w-full border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none"
              data-testid="image-insert-alt"
            />
          </div>

          {/* Alignment */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Alignment</label>
            <div className="mt-2 flex gap-2">
              {radio("Left", "left", align, setAlign, "image-align-left")}
              {radio("Centre", "center", align, setAlign, "image-align-center")}
              {radio("Right", "right", align, setAlign, "image-align-right")}
            </div>
          </div>

          {/* Layout */}
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Text flow</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {radio("Above and below (block)", "block", layout, setLayout, "image-layout-block")}
              {radio("Wrap text around image", "wrap", layout, setLayout, "image-layout-wrap")}
            </div>
            {layout === "wrap" && (
              <p className="mt-2 mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                Tip: place your cursor at the start of a paragraph, then insert — the text will flow around the image.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="mono text-xs uppercase tracking-[0.2em] text-neutral-600 hover:text-black"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={uploading || !file}
            data-testid="image-insert-submit"
            className="bg-black text-white px-6 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-amber-700 disabled:opacity-40"
          >
            {uploading ? "Uploading…" : "Upload & insert"}
          </button>
        </div>
      </div>
    </div>
  );
}
